/**
 * regimeModifier.ts -- MER Algo Footprints
 *
 * Phase 2 v1: regime modifier detector.
 *
 * A "regime modifier" is a transient, high-information signal that fires
 * alongside the existing macro RegimeDominant tournament. Modifiers do
 * NOT replace dominant -- they ride alongside.
 *
 * Concept:
 *
 *   Macro regime (today's tournament: meanReversion | breakout | trend |
 *   noTrade) is a slow-moving classification of how to interpret price
 *   action over a 5-15min window. A "Trend Continuation" macro regime
 *   can still have a sweep firing right now, or a trap reversal in
 *   progress, or gamma pinning the price at a wall. These are micro
 *   conditions that briefly override or qualify the macro read.
 *
 *   Modifiers v1 surface these micro conditions to the AI without
 *   changing the existing regime scoring. The AI sees both pieces of
 *   information and can reason about them.
 *
 * v1 scope:
 *
 *   - Five modifier types (priority order):
 *     1. trap_reversal       -- High-severity trap signal active
 *     2. liquidity_sweep     -- SMC sweepAndReverse with directional bias
 *     3. gamma_pin           -- Long gamma + price near pinStrike
 *     4. volatility_expansion -- rangeRel > 1.5% AND rvol > 1.5
 *     5. volatility_compression -- rangeRel < 0.20% AND rvol < 0.7
 *
 *   - Singular output (one modifier or null). Priority order resolves
 *     conflicts; multi-modifier output deferred to v2 if needed.
 *
 *   - No hysteresis on modifier. By design transient.
 *
 *   - Confidence cap 0.90 (matches confluence/flow convention).
 *
 *   - Sub-0.30 guard: detectors that can decay (gamma_pin, vol_*)
 *     return null when confidence drops below 0.30 to avoid surfacing
 *     stale or weak modifiers as if they were meaningful.
 *
 *   - Trap age decay deferred to v2 (TrapSignal has no fired-at field).
 *     trap_reversal confidence is severity baseline only (0.75 high,
 *     0.45 medium) since the trap engine emits only currently-active
 *     traps by construction.
 *
 *   - INFORMATIONAL ONLY in v1. Modifier appears in RegimeResult and
 *     in the prompt; confluence weighting and the macro regime
 *     tournament are NOT changed by the modifier. Confluence weight
 *     adjustments (if any) defer to v2 after we observe AI reasoning
 *     with modifiers in production.
 */

import type { Candle, GammaResult, SMCResult, TrapSignal } from '../../../../types';
import { calcRelVol } from '../../../../utils/calc';

export type RegimeModifier =
  | 'trap_reversal'
  | 'liquidity_sweep'
  | 'gamma_pin'
  | 'volatility_expansion'
  | 'volatility_compression';

export interface RegimeModifierDetail {
  type:       RegimeModifier;
  severity:   'high' | 'medium';
  confidence: number;   // 0..0.90, derived per-modifier (see comments below)
  reason:     string;   // one-line attribution for prompt + audit
}

// Confidence cap (matches confluence/flow convention -- never deterministic certainty)
const MODIFIER_CONFIDENCE_CAP = 0.90;

// Sub-threshold cutoff: applied to modifiers whose confidence can decay or
// be distance-derived. Below this, return null rather than surfacing a
// stale/weak signal that would just add noise.
const MIN_USEFUL_CONFIDENCE   = 0.30;

// Volatility expansion / compression thresholds. Stricter than the existing
// regime volatility-component thresholds (rangeRel 1.2 / 0.25) because
// modifiers should fire on genuinely distinctive conditions, not on every
// elevated session.
const EXPANSION_RANGE_MIN     = 1.5;   // %
const EXPANSION_RANGE_FULL    = 2.5;   // % -- confidence reaches 1.0 here
const EXPANSION_RVOL_MIN      = 1.5;
const COMPRESSION_RANGE_MAX   = 0.20;  // %
const COMPRESSION_RANGE_FULL  = 0.10;  // % -- confidence reaches 1.0 here
const COMPRESSION_RVOL_MAX    = 0.70;

// Gamma pin: price must be within this fraction of pinStrike for the
// modifier to fire. Distance scaled inversely within the range.
const GAMMA_PIN_DIST_PCT      = 0.005;  // 0.5%

function clampConfidence(c: number): number {
  if (!isFinite(c) || c <= 0) return 0;
  return Math.min(MODIFIER_CONFIDENCE_CAP, c);
}

// ── Detectors (priority order) ──────────────────────────────────────────────
//
// Each detector returns RegimeModifierDetail when its conditions fire,
// or null when they don't. The main detectRegimeModifier function calls
// these in priority order and returns the first non-null result.

/**
 * Priority 1: trap_reversal
 *
 * Fires when the trap engine emits a High-severity trap signal with a
 * directional side (bull or bear). The trap engine only emits currently-
 * active traps by construction, so freshness is implicit.
 *
 * Confidence: severity baseline only.
 *   - High   -> 0.75
 *   - Medium -> 0.45  (note: Medium traps don't trigger this modifier
 *                     in v1 -- see priority filter below)
 *
 * v1 only surfaces HIGH-severity traps as a regime modifier. Medium
 * traps stay in the regular trap panel but don't drive regime context.
 * Rationale: regime modifiers should mark distinctive conditions.
 * Medium traps fire too often to qualify as a regime-level shift.
 */
function detectTrapReversal(traps: TrapSignal[] | null | undefined): RegimeModifierDetail | null {
  if (!Array.isArray(traps) || traps.length === 0) return null;

  // Find the highest-severity directional trap (bull or bear; not neutral).
  // If multiple traps share top severity with conflicting sides, that's
  // ambiguous and we return null rather than pick one arbitrarily.
  const directional = traps.filter(t => t && (t.side === 'bull' || t.side === 'bear'));
  if (directional.length === 0) return null;

  const highSev = directional.filter(t => t.severity === 'High');
  if (highSev.length === 0) return null;

  // Conflicting top-severity directional traps -> ambiguous, no modifier
  const sides = new Set(highSev.map(t => t.side));
  if (sides.size > 1) return null;

  const t = highSev[0];
  const levelStr = t.level != null ? ` @ ${t.level.toFixed(2)}` : '';
  return {
    type:       'trap_reversal',
    severity:   'high',
    confidence: 0.75,
    reason:     `High-severity ${t.side} trap: ${t.type}${levelStr}`,
  };
}

/**
 * Priority 2: liquidity_sweep
 *
 * Fires when SMC reports sweepAndReverse with a directional bias.
 * Neutral SMC bias + sweep does not qualify because the directional
 * implication is missing.
 *
 * Confidence: SMC strength baseline.
 *   - major -> 0.70
 *   - minor -> 0.45
 */
function detectLiquiditySweep(smc: SMCResult | null | undefined): RegimeModifierDetail | null {
  if (!smc) return null;
  if (!smc.sweepAndReverse) return null;
  if (smc.bias === 'neutral') return null;

  const strong = smc.biasStrength === 'major';
  return {
    type:       'liquidity_sweep',
    severity:   strong ? 'high' : 'medium',
    confidence: strong ? 0.70 : 0.45,
    reason:     `SMC ${smc.bias} ${smc.biasStrength} sweep+reclaim: ${smc.keySignal}`,
  };
}

/**
 * Priority 3: gamma_pin
 *
 * Fires when:
 *   - gamma regime contains "Long Gamma" (dealers suppress vol)
 *   - pinStrike is defined
 *   - current price is within GAMMA_PIN_DIST_PCT of pinStrike
 *
 * Confidence: distance-weighted, scaled by gamma engine's own
 * InferenceConfidence. Below MIN_USEFUL_CONFIDENCE => null.
 */
function detectGammaPin(
  gamma:    GammaResult | null | undefined,
  curPrice: number,
): RegimeModifierDetail | null {
  if (!gamma) return null;
  if (!gamma.regime.includes('Long')) return null;
  if (gamma.pinStrike == null || !isFinite(gamma.pinStrike) || gamma.pinStrike <= 0) return null;

  const distPct = Math.abs(curPrice - gamma.pinStrike) / gamma.pinStrike;
  if (distPct > GAMMA_PIN_DIST_PCT) return null;

  // Distance score: 1.0 at the pin, 0.0 at the edge of the band.
  const distScore = 1.0 - (distPct / GAMMA_PIN_DIST_PCT);

  // Scale by gamma engine's own confidence
  const gammaConfMul: Record<string, number> = { High: 1.0, Medium: 0.7, Low: 0.4 };
  const mul = gammaConfMul[gamma.confidence] ?? 0.5;

  const conf = clampConfidence(distScore * mul);
  if (conf < MIN_USEFUL_CONFIDENCE) return null;

  const sev: 'high' | 'medium' = gamma.confidence === 'High' ? 'high' : 'medium';
  return {
    type:       'gamma_pin',
    severity:   sev,
    confidence: conf,
    reason:     `Long gamma pinning near ${gamma.pinStrike.toFixed(2)} (${(distPct * 100).toFixed(2)}% away)`,
  };
}

/**
 * Priority 4: volatility_expansion
 *
 * Fires on stricter thresholds than the existing regime volatility
 * scoring -- modifier should mark distinctive conditions, not every
 * elevated session.
 *
 * Confidence: linear ramp between rangeRel 1.5 (0.0) and 2.5 (1.0),
 * gated by rvol >= 1.5. Below MIN_USEFUL_CONFIDENCE => null.
 */
function detectVolatilityExpansion(
  candles: Candle[],
  rvol:    number,
): RegimeModifierDetail | null {
  if (candles.length < 10) return null;
  if (!isFinite(rvol) || rvol < EXPANSION_RVOL_MIN) return null;

  const last10 = candles.slice(-Math.min(10, candles.length));
  const avgRange = last10.reduce((s, c) => s + (c.high - c.low), 0) / last10.length;
  const curPrice = candles[candles.length - 1].close;
  if (curPrice <= 0) return null;
  const rangeRel = (avgRange / curPrice) * 100;

  if (rangeRel < EXPANSION_RANGE_MIN) return null;

  const span = EXPANSION_RANGE_FULL - EXPANSION_RANGE_MIN;
  const conf = clampConfidence((rangeRel - EXPANSION_RANGE_MIN) / span);
  if (conf < MIN_USEFUL_CONFIDENCE) return null;

  const sev: 'high' | 'medium' = rangeRel >= EXPANSION_RANGE_FULL ? 'high' : 'medium';
  return {
    type:       'volatility_expansion',
    severity:   sev,
    confidence: conf,
    reason:     `Range/bar ${rangeRel.toFixed(2)}% with RVOL ${rvol.toFixed(2)}x -- volatility expansion`,
  };
}

/**
 * Priority 5: volatility_compression
 *
 * Mirror of expansion, looking for coiled/compressed conditions that
 * often precede a breakout. rangeRel must be below COMPRESSION_RANGE_MAX
 * AND rvol below COMPRESSION_RVOL_MAX. Linear ramp on rangeRel from
 * 0.20 (0.0) down to 0.10 (1.0).
 *
 * Severity capped at 'medium' in v1 -- compression rarely warrants
 * 'high' framing until we have outcome data showing otherwise.
 */
function detectVolatilityCompression(
  candles: Candle[],
  rvol:    number,
): RegimeModifierDetail | null {
  if (candles.length < 10) return null;
  if (!isFinite(rvol) || rvol > COMPRESSION_RVOL_MAX) return null;

  const last10 = candles.slice(-Math.min(10, candles.length));
  const avgRange = last10.reduce((s, c) => s + (c.high - c.low), 0) / last10.length;
  const curPrice = candles[candles.length - 1].close;
  if (curPrice <= 0) return null;
  const rangeRel = (avgRange / curPrice) * 100;

  if (rangeRel > COMPRESSION_RANGE_MAX) return null;

  const span = COMPRESSION_RANGE_MAX - COMPRESSION_RANGE_FULL;
  const conf = clampConfidence((COMPRESSION_RANGE_MAX - rangeRel) / span);
  if (conf < MIN_USEFUL_CONFIDENCE) return null;

  return {
    type:       'volatility_compression',
    severity:   'medium',
    confidence: conf,
    reason:     `Range/bar ${rangeRel.toFixed(2)}% with RVOL ${rvol.toFixed(2)}x -- volatility compression / coiling`,
  };
}

// ── Public entry point ──────────────────────────────────────────────────────

/**
 * Detect the single most relevant regime modifier for the current state.
 *
 * Priority order:
 *   1. trap_reversal       (high-severity directional trap active)
 *   2. liquidity_sweep     (SMC sweep+reclaim with directional bias)
 *   3. gamma_pin           (long gamma + near pinStrike)
 *   4. volatility_expansion (range + rvol expansion)
 *   5. volatility_compression (range + rvol compression)
 *
 * Returns null when no modifier qualifies.
 *
 * @param candles     Bar series (last bar = current)
 * @param rvol        Pre-computed relative volume (passed in to avoid double-compute)
 * @param smc         SMC engine result (or null)
 * @param gamma       Gamma engine result (or null)
 * @param traps       Trap signals (or null) -- new input vs pre-v1 regime
 */
export function detectRegimeModifier(
  candles: Candle[],
  rvol:    number,
  smc:     SMCResult | null | undefined,
  gamma:   GammaResult | null | undefined,
  traps:   TrapSignal[] | null | undefined,
): RegimeModifierDetail | null {
  if (!candles || candles.length === 0) return null;
  const curPrice = candles[candles.length - 1].close;

  // Priority order. First non-null wins.
  return (
    detectTrapReversal(traps)                       ??
    detectLiquiditySweep(smc)                       ??
    detectGammaPin(gamma, curPrice)                 ??
    detectVolatilityExpansion(candles, rvol)        ??
    detectVolatilityCompression(candles, rvol)      ??
    null
  );
}

// Exported for tests
export const _internal = {
  MODIFIER_CONFIDENCE_CAP,
  MIN_USEFUL_CONFIDENCE,
  EXPANSION_RANGE_MIN,
  EXPANSION_RANGE_FULL,
  EXPANSION_RVOL_MIN,
  COMPRESSION_RANGE_MAX,
  COMPRESSION_RANGE_FULL,
  COMPRESSION_RVOL_MAX,
  GAMMA_PIN_DIST_PCT,
  detectTrapReversal,
  detectLiquiditySweep,
  detectGammaPin,
  detectVolatilityExpansion,
  detectVolatilityCompression,
};

// Re-export rvol calc consumers can use to mirror regime engine's choice.
export { calcRelVol };
